// Smart Sash Admin Panel - gRPC Event Service
// Manages gRPC server-streaming events from EventService

import {
  ChannelType,
  EventMessage,
  EventServiceDefinition,
  SubscriptionRequest,
} from '@/generated/Common/EventService';
import { Empty } from '@/generated/google/protobuf/empty';
import { grpc } from '@improbable-eng/grpc-web';

/**
 * Event handler type
 */
type EventHandler<T = unknown> = (data: T) => void;

class SubscriptionRequestMessage implements grpc.ProtobufMessage {
  constructor(private readonly value: SubscriptionRequest = {} as SubscriptionRequest) {}

  serializeBinary(): Uint8Array {
    return SubscriptionRequest.encode(this.value).finish();
  }

  toObject(): SubscriptionRequest {
    return this.value;
  }

  static deserializeBinary(bytes: Uint8Array): SubscriptionRequestMessage {
    return new SubscriptionRequestMessage(SubscriptionRequest.decode(bytes));
  }
}

class EventMessageMessage implements grpc.ProtobufMessage {
  constructor(private readonly value: EventMessage = {} as EventMessage) {}

  serializeBinary(): Uint8Array {
    return EventMessage.encode(this.value).finish();
  }

  toObject(): EventMessage {
    return this.value;
  }

  static deserializeBinary(bytes: Uint8Array): EventMessageMessage {
    return new EventMessageMessage(EventMessage.decode(bytes));
  }
}

class EmptyMessage implements grpc.ProtobufMessage {
  constructor(private readonly value: Empty = {}) {}

  serializeBinary(): Uint8Array {
    return Empty.encode(this.value).finish();
  }

  toObject(): Empty {
    return this.value;
  }

  static deserializeBinary(bytes: Uint8Array): EmptyMessage {
    return new EmptyMessage(Empty.decode(bytes));
  }
}

/**
 * gRPC Event Service Class
 * Manages real-time event streaming via gRPC Web
 */
class GrpcEventService {
  private grpcUrl: string | null = null;
  private activeStream: grpc.Request | null = null;
  private eventHandlers: Map<string, Array<EventHandler<unknown>>> = new Map();
  private sessionId: string | null = null;
  private institutionId: string | null = null;
  private channelType: ChannelType = ChannelType.ADMIN;

  /**
   * Initialize gRPC client for browser environment
   */
  initialize(grpcUrl: string): void {
    if (this.grpcUrl) {
      console.warn('gRPC client already initialized');
      return;
    }

    this.grpcUrl = grpcUrl;
    grpc.setDefaultTransport(grpc.FetchReadableStreamTransport({}));
    console.log('gRPC Event Service initialized for browser');
  }

  /**
   * Subscribe to event stream
   */
  async subscribe(
    sessionId: string,
    institutionId: string,
    channel: ChannelType = ChannelType.ADMIN
  ): Promise<void> {
    if (!this.grpcUrl) {
      throw new Error('gRPC client not initialized. Call initialize() first.');
    }

    if (this.activeStream) {
      console.warn('Already subscribed. Unsubscribe first.');
      return;
    }

    try {
      this.sessionId = sessionId;
      this.institutionId = institutionId;
      this.channelType = channel;

      const request: SubscriptionRequest = {
        sessionId,
        channel,
        institutionId,
      };

      const subscribeDescriptor: grpc.MethodDefinition<
        SubscriptionRequestMessage,
        EventMessageMessage
      > = {
        methodName: EventServiceDefinition.methods.subscribe.name,
        service: { serviceName: EventServiceDefinition.fullName },
        requestStream: false,
        responseStream: true,
        requestType: SubscriptionRequestMessage,
        responseType: EventMessageMessage,
      };

      console.log('Subscribing to gRPC event stream', {
        sessionId,
        institutionId,
        channel,
      });

      this.activeStream = grpc.invoke(subscribeDescriptor, {
        host: this.grpcUrl,
        request: new SubscriptionRequestMessage(request),
        onMessage: (message) => {
          const eventMessage = (message as unknown as EventMessageMessage).toObject();
          this.handleEventMessage(eventMessage);
        },
        onEnd: (code, message, _trailers) => {
          this.activeStream = null;
          if (code !== grpc.Code.OK) {
            this.handleStreamError(new Error(`gRPC stream ended (${code}): ${message}`));
          }
        },
      });
    } catch (error) {
      console.error('Failed to subscribe to gRPC event stream:', error);
      throw error;
    }
  }

  /**
   * Handle incoming event message
   */
  private handleEventMessage(message: EventMessage): void {
    const { eventType, eventData } = message;

    console.log('Received gRPC event:', eventType, eventData);

    // Notify registered handlers for this event type
    const handlers = this.eventHandlers.get(eventType) || [];
    handlers.forEach((handler) => {
      try {
        // Extract the actual data from google.protobuf.Any
        const data = eventData?.value ? this.decodeEventData(eventType, eventData.value) : null;
        (handler as EventHandler<unknown>)(data);
      } catch (error) {
        console.error(`Error handling event ${eventType}:`, error);
      }
    });
  }

  /**
   * Decode event data — returns the raw bytes so callers can use the correct
   * generated protobuf decoder for each event type.
   */
  private decodeEventData(_eventType: string, value: Uint8Array): unknown {
    return value;
  }

  /**
   * Handle stream errors
   */
  private handleStreamError(error: Error): void {
    // Cleanup on error
    this.activeStream = null;

    // Notify error handlers if registered
    const errorHandlers = this.eventHandlers.get('error') || [];
    errorHandlers.forEach((handler) => handler(error));
  }

  /**
   * Register event handler
   */
  on<T = unknown>(eventType: string, handler: EventHandler<T>): void {
    const handlers = this.eventHandlers.get(eventType) ?? [];
    handlers.push(handler as EventHandler<unknown>);
    this.eventHandlers.set(eventType, handlers);
  }

  /**
   * Unregister event handler
   */
  off(eventType: string, handler?: EventHandler): void {
    if (!handler) {
      // Remove all handlers for this event
      this.eventHandlers.delete(eventType);
      return;
    }

    const handlers = this.eventHandlers.get(eventType) || [];
    const index = handlers.indexOf(handler);
    if (index > -1) {
      handlers.splice(index, 1);
      if (handlers.length === 0) {
        this.eventHandlers.delete(eventType);
      } else {
        this.eventHandlers.set(eventType, handlers);
      }
    }
  }

  /**
   * Unsubscribe from event stream
   */
  async unsubscribe(): Promise<void> {
    if (!this.grpcUrl) {
      console.warn('gRPC client not initialized');
      return;
    }

    // Cancel stream
    if (this.activeStream) {
      this.activeStream.close();
      this.activeStream = null;
    }

    // Call unsubscribe on server if we have an active session
    if (this.sessionId && this.institutionId) {
      const request: SubscriptionRequest = {
        sessionId: this.sessionId,
        channel: this.channelType,
        institutionId: this.institutionId,
      };

      const unsubscribeDescriptor: grpc.UnaryMethodDefinition<
        SubscriptionRequestMessage,
        EmptyMessage
      > = {
        methodName: EventServiceDefinition.methods.unsubscribe.name,
        service: { serviceName: EventServiceDefinition.fullName },
        requestStream: false,
        responseStream: false,
        requestType: SubscriptionRequestMessage,
        responseType: EmptyMessage,
      };

      await new Promise<void>((resolve, reject) => {
        grpc.unary(unsubscribeDescriptor, {
          host: this.grpcUrl!,
          request: new SubscriptionRequestMessage(request),
          onEnd: (output) => {
            if (output.status === grpc.Code.OK) {
              console.log('Unsubscribed from gRPC event stream');
              resolve();
              return;
            }

            reject(new Error(`Failed to unsubscribe (${output.status}): ${output.statusMessage}`));
          },
        });
      }).finally(() => {
        this.sessionId = null;
        this.institutionId = null;
      });
    }
  }

  /**
   * Check if currently subscribed
   */
  isSubscribed(): boolean {
    return this.activeStream !== null;
  }

  /**
   * Cleanup and close connection
   */
  async dispose(): Promise<void> {
    await this.unsubscribe();
    this.eventHandlers.clear();
    this.grpcUrl = null;
    console.log('gRPC Event Service disposed');
  }
}

// Create singleton instance
export const grpcEventService = new GrpcEventService();

export default grpcEventService;
